import { useState, useEffect, useCallback, useRef } from 'react';
import {
  isCapacitorNative,
  getAppVersion,
  getApiBaseUrl,
  onAppStateChange,
} from '@/lib/capacitor';
import {
  installUpdateRelease,
  onUpdateDownloadProgress,
  resumeDownloadedUpdateInstall,
} from '@/lib/native-updater';

export interface UpdateInfo {
  versionCode: number;
  versionName: string;
  tag?: string;
  releaseNotes: string;
  downloadUrl: string;
  downloadEnabled: boolean;
  fileSize?: number | null;
  environment?: string;
}

export type UpdateInstallState =
  | { kind: 'idle' }
  | {
      kind: 'downloading';
      bytesReceived: number;
      totalBytes: number;
      progress: number;
    }
  | {
      kind: 'awaitingPermission';
      downloadedPath: string;
      autoResumeOnActive: boolean;
    }
  | { kind: 'installing' }
  | {
      kind: 'error';
      phase: 'download' | 'install';
      message: string;
      downloadedPath?: string;
    };

interface UseUpdateCheckResult {
  isUpdateModalOpen: boolean;
  latestVersion: UpdateInfo | null;
  currentVersion: { versionCode: number; versionName: string } | null;
  isChecking: boolean;
  error: string | null;
  installState: UpdateInstallState;
  dismissUpdate: () => void;
  startUpdateInstall: () => Promise<void>;
  checkForUpdate: () => Promise<void>;
}

export function useUpdateCheck(): UseUpdateCheckResult {
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [latestVersion, setLatestVersion] = useState<UpdateInfo | null>(null);
  const [currentVersion, setCurrentVersion] = useState<{ versionCode: number; versionName: string } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installState, setInstallState] = useState<UpdateInstallState>({ kind: 'idle' });

  const latestVersionRef = useRef<UpdateInfo | null>(null);
  const installStateRef = useRef<UpdateInstallState>({ kind: 'idle' } as UpdateInstallState);

  useEffect(() => {
    latestVersionRef.current = latestVersion;
  }, [latestVersion]);

  useEffect(() => {
    installStateRef.current = installState;
  }, [installState]);

  const checkForUpdate = useCallback(async (options?: { ignoreDismissed?: boolean }) => {
    if (!isCapacitorNative()) {
      return;
    }

    const activeInstallState = installStateRef.current;
    if (
      activeInstallState.kind === 'downloading' ||
      activeInstallState.kind === 'awaitingPermission' ||
      activeInstallState.kind === 'installing'
    ) {
      return;
    }

    if (dismissed && !options?.ignoreDismissed) {
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const appVersion = await getAppVersion();
      if (!appVersion) {
        return;
      }

      setCurrentVersion(appVersion);

      const response = await fetch(`${getApiBaseUrl()}/api/app/version`);
      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }

      const data = await response.json();
      if (!data.success || !data.version) {
        throw new Error('Invalid version response');
      }

      const serverVersion: UpdateInfo = data.version;

      if (serverVersion.versionCode > appVersion.versionCode && serverVersion.downloadEnabled) {
        setLatestVersion(serverVersion);
        if (!dismissed || options?.ignoreDismissed) {
          setIsUpdateModalOpen(true);
        }
      } else {
        setLatestVersion(null);
        setInstallState({ kind: 'idle' });
        setIsUpdateModalOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check for updates');
    } finally {
      setIsChecking(false);
    }
  }, [dismissed]);

  const runUpdateInstall = useCallback(async (
    existingPath?: string,
    options?: { armAutoResumeOnActive?: boolean },
  ) => {
    const update = latestVersionRef.current;
    if (!update) {
      return;
    }

    setError(null);

    const startingTotalBytes = update.fileSize ?? 0;
    let totalBytes = startingTotalBytes;
    let bytesReceived = existingPath ? startingTotalBytes : 0;
    let progressHandle: { remove: () => Promise<void> | void } | null = null;

    if (existingPath) {
      setInstallState({ kind: 'installing' });
    } else {
      setInstallState({
        kind: 'downloading',
        bytesReceived: 0,
        totalBytes: startingTotalBytes,
        progress: 0,
      });

      progressHandle = await onUpdateDownloadProgress((progress) => {
        totalBytes = progress.totalBytes ?? update.fileSize ?? totalBytes;
        bytesReceived = progress.bytesReceived;
        setInstallState({
          kind: 'downloading',
          bytesReceived,
          totalBytes,
          progress: progress.progress,
        });
      });
    }

    try {
      const result = existingPath
        ? await resumeDownloadedUpdateInstall(existingPath)
        : await installUpdateRelease(update);

      if (result.status === 'downloaded-awaiting-permission') {
        setInstallState({
          kind: 'awaitingPermission',
          downloadedPath: result.path,
          autoResumeOnActive: options?.armAutoResumeOnActive ?? true,
        });
        setIsUpdateModalOpen(true);
        return;
      }

      setInstallState({ kind: 'idle' });
      setIsUpdateModalOpen(false);
    } catch (err) {
      setInstallState({
        kind: 'error',
        phase: existingPath ? 'install' : 'download',
        message: err instanceof Error ? err.message : 'Update failed',
        downloadedPath: existingPath,
      });
      setIsUpdateModalOpen(true);
    } finally {
      if (progressHandle) {
        await Promise.resolve(progressHandle.remove()).catch(() => undefined);
      }
    }
  }, []);

  const startUpdateInstall = useCallback(async () => {
    const activeInstallState = installStateRef.current;
    if (activeInstallState.kind === 'downloading' || activeInstallState.kind === 'installing') {
      return;
    }

    const existingPath =
      activeInstallState.kind === 'awaitingPermission' || activeInstallState.kind === 'error'
        ? activeInstallState.downloadedPath
        : undefined;

    await runUpdateInstall(existingPath, { armAutoResumeOnActive: true });
  }, [runUpdateInstall]);

  const dismissUpdate = useCallback(() => {
    setIsUpdateModalOpen(false);
    if (installStateRef.current.kind === 'idle') {
      setDismissed(true);
    }
  }, []);

  const handleAppStateChange = useCallback((isActive: boolean) => {
    if (!isActive) {
      return;
    }

    const activeInstallState = installStateRef.current;
    if (activeInstallState.kind === 'awaitingPermission') {
      if (!activeInstallState.autoResumeOnActive) {
        setIsUpdateModalOpen(true);
        return;
      }

      setInstallState({
        kind: 'awaitingPermission',
        downloadedPath: activeInstallState.downloadedPath,
        autoResumeOnActive: false,
      });
      void runUpdateInstall(activeInstallState.downloadedPath, { armAutoResumeOnActive: false });
      return;
    }

    if (activeInstallState.kind === 'downloading' || activeInstallState.kind === 'installing') {
      return;
    }

    setDismissed(false);
    void checkForUpdate({ ignoreDismissed: true });
  }, [checkForUpdate, runUpdateInstall]);

  useEffect(() => {
    void checkForUpdate();
  }, [checkForUpdate]);

  useEffect(() => {
    if (!isCapacitorNative()) {
      return;
    }

    const cleanup = onAppStateChange(handleAppStateChange);
    return cleanup;
  }, [handleAppStateChange]);

  return {
    isUpdateModalOpen,
    latestVersion,
    currentVersion,
    isChecking,
    error,
    installState,
    dismissUpdate,
    startUpdateInstall,
    checkForUpdate: () => checkForUpdate(),
  };
}
